/**
 * Spots SMS conversations whose text never reached the customer, so the
 * Conversations screen can show a red "Failed" tile and say why.
 *
 * The API marks these conversations Status "Error" with an ErrorMessage of
 * "Delivery failed: <ClickSend's reason>" (deliveryReceipt in comms.js).
 * Older conversations, from before that change, may still be Active with a
 * sent message whose DeliveryStatus is "Failed", so both are checked.
 */

const PREFIX = /^delivery failed:\s*/i;
const DEAD_NUMBER = /unknown subscriber|invalid|inactive|absent subscriber|not in use|barred|unallocated|does not exist|no such/i;
const EXPIRED = /expired|validity/i;

const field = (obj, ...names) => {
  for (const name of names) {
    if (obj && obj[name] != null && obj[name] !== '') return obj[name];
  }
  return null;
};

/** The provider's own words, without our "Delivery failed:" prefix. */
const providerReason = (text) => String(text || '').replace(PREFIX, '').trim();

/** A plain-English sentence for staff, from the provider's reason. */
export const explainFailure = (text) => {
  const reason = providerReason(text) || 'no reason given';
  if (DEAD_NUMBER.test(reason)) {
    return 'The number is invalid or no longer in use, so the text could not be delivered. '
      + 'The automated flow has stopped. Please find another way to reach the customer, or ask for a new number.';
  }
  if (EXPIRED.test(reason)) {
    return 'The network never confirmed delivery before the text expired. The phone may have been off or out of signal for days, '
      + 'or the number may no longer be in use. The automated flow has stopped. Please call the customer or try again in a few days.';
  }
  return 'The text could not be delivered. The automated flow has stopped. Please check the number and contact the customer another way.';
};

/**
 * Returns null when nothing failed, or { reason, explanation } where reason is
 * the provider's wording (for example "Unknown subscriber").
 */
export const getDeliveryFailure = (conversation) => {
  if (!conversation) return null;
  const status = field(conversation, 'Status', 'status');
  const error = field(conversation, 'ErrorMessage', 'errorMessage');

  if (status === 'Error' && error && PREFIX.test(String(error))) {
    const reason = providerReason(error) || 'no reason given';
    return { reason, explanation: explainFailure(error) };
  }

  // Older data: a sent message marked Failed, while the conversation is still open or was
  // set to Error without a reason being saved (some from 17 Sep 2026 are like this).
  if (status === 'Active' || status === 'AwaitingHuman' || status === 'Error') {
    const messages = field(conversation, 'messages') || [];
    const failed = messages.find((m) => field(m, 'MessageType', 'messageType') === 'Sent'
      && String(field(m, 'DeliveryStatus', 'deliveryStatus') || '').toLowerCase() === 'failed');
    if (failed) {
      const text = field(failed, 'ErrorMessage', 'errorMessage');
      return { reason: providerReason(text) || 'no reason given', explanation: explainFailure(text) };
    }
  }
  return null;
};

/* ------------------------------------------------------------------ *
 * Opt-outs and replies                                                *
 * ------------------------------------------------------------------ */

const parseMetadata = (conversation) => {
  const raw = field(conversation, 'Metadata', 'metadata');
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw) || {}; } catch (e) { return {}; }
};

// The same whole-message phrases the API treats as an opt-out (sms-reply-rules.js).
const OPT_OUT_PHRASES = new Set(['stop', 'stopall', 'stop all', 'unsubscribe', 'unsubscribe me', 'opt out', 'optout',
  'remove me', 'remove my number', 'dont text me', 'do not text me', 'no more texts']);
const normalise = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
export const isOptOutText = (text) => {
  const reply = normalise(text);
  if (!reply) return false;
  if (OPT_OUT_PHRASES.has(reply)) return true;
  const words = reply.split(' ');
  return words[0] === 'stop' && words.length <= 6;
};

const received = (conversation) => (field(conversation, 'messages') || [])
  .filter((m) => field(m, 'MessageType', 'messageType') === 'Received');

/** Returns null, or { at, text } when the customer has opted out of texts. */
export const getOptOut = (conversation) => {
  if (!conversation) return null;
  const meta = parseMetadata(conversation);
  if (meta.optOut) return { at: meta.optOut.at || null, text: meta.optOut.text || 'STOP' };
  const reply = received(conversation).find((m) => isOptOutText(field(m, 'MessageText', 'messageText')));
  return reply ? { at: field(reply, 'SentAt', 'sentAt'), text: field(reply, 'MessageText', 'messageText') } : null;
};

export const hasReplied = (conversation) => received(conversation).length > 0;

/**
 * What the status tile shows, in priority order:
 *   OPT OUT (Action Required) - red, the customer replied STOP or similar
 *   Failed                    - red, the text never reached them
 *   Sent                      - blue, Active but no reply yet
 *   Active                    - green, Active and they have replied at least once
 *   Replied (Human Managed)   - green, with a person and they have replied
 * Anything else keeps its normal status tile (returns null).
 */
export const getDisplayStatus = (conversation) => {
  if (!conversation) return null;
  if (getOptOut(conversation)) return { label: 'OPT OUT (Action Required)', variant: 'danger', red: true };
  if (getDeliveryFailure(conversation)) return { label: 'Failed', variant: 'danger', red: true };
  if (field(conversation, 'Status', 'status') === 'Error' && !field(conversation, 'ErrorMessage', 'errorMessage')) {
    return { label: 'Error (no reason recorded)', variant: 'danger' };
  }
  if (field(conversation, 'Status', 'status') === 'Active') {
    return hasReplied(conversation) ? { label: 'Active', variant: 'success' } : { label: 'Sent', variant: 'primary' };
  }
  // Replies usually move a conversation to a person (AwaitingHuman), so green has to cover
  // those too or it never shows (17 Oct 2026: 0 Active conversations had a reply).
  if (field(conversation, 'Status', 'status') === 'AwaitingHuman' && hasReplied(conversation)) {
    return { label: 'Replied (Human Managed)', variant: 'success' };
  }
  return null;
};
